import { FCC } from "@/types/common";
import { Stack, styled, Typography, Link, TypographyProps } from "@mui/material";
import { EMPTY_STR } from "@/constants/string";
import Divider from "@mui/material/Divider";
import { black, red, text } from "@/theme/colors";
import { DATE_FORMAT } from "@/constants/datetime";
import { convertNumberToZipCode } from "@/utils/input";
import { getExternalLink } from "@/utils/url";
import dayjs from "dayjs";
import { useFileInfo } from "@/api/services/global/file";
import { FileItem } from "@/components/elements/FileItem";

type TItemInfo = {
  label?: string;
  value?: string | null | undefined;
  direction?: "row" | "column";
  type?: "string" | "date" | "number" | "zipcode" | "textArea" | "link" | "file";
  color?: string;
  suffix?: string;
  required?: boolean;
  endIcon?: React.ReactNode;
  isShowDivider?: boolean;
  variant?: TypographyProps["variant"];
};

export const ItemInfo: FCC<TItemInfo> = ({
  label,
  value,
  direction,
  type = "string",
  color,
  suffix,
  endIcon,
  required = false,
  isShowDivider = true,
  variant = "body14Semi",
}) => {
  const getValue = () => {
    if (!value) return EMPTY_STR.TEXT;
    if (type === "zipcode") {
      return convertNumberToZipCode(value);
    }
    if (type === "date") {
      return dayjs(value).isValid() ? dayjs(value).format(DATE_FORMAT) : EMPTY_STR.DATE;
    }
    if (type === "number") {
      return Number(value) || 0;
    }
    return value;
  };

  const RenderValue: FCC = ({ children }) => {
    return (
      <Typography
        variant={variant}
        color={color ? color : text.primary}
        sx={{
          fontFamily: "inherit",
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          overflowWrap: "break-word",
        }}
      >
        {children}
      </Typography>
    );
  };

  const RenderComponent: FCC = ({}) => {
    if (type === "link") {
      return (
        <RenderValue>
          {value ? (
            <Link href={getExternalLink(value)} target="_blank" underline="none" color={color} rel="noopener">
              {getValue()}
            </Link>
          ) : (
            getValue()
          )}
        </RenderValue>
      );
    }
    if (type === "textArea") {
      if (!value) return EMPTY_STR.TEXT;
      const urlRegex = /(https?:\/\/[^\s]+)/g;
      const parts = value.split(urlRegex);

      return (
        <pre
          style={{
            whiteSpace: "pre-wrap",
            wordBreak: "break-word",
            fontSize: 14,
            fontFamily: "inherit",
          }}
        >
          {parts.map((part, index) =>
            urlRegex.test(part) ? (
              <Link key={index} href={part} target="_blank" rel="noopener noreferrer" underline="none" color="primary">
                {part}
              </Link>
            ) : (
              part
            ),
          )}
        </pre>
      );
    }
    if (type === "file") {
      const { isLoading } = useFileInfo(value ?? "");

      if (isLoading) return null;

      return <RenderValue>{value ? <FileItem filePath={value} downloadable hideIcon={true} /> : getValue()}</RenderValue>;
    }
    return (
      <RenderValue>
        {getValue()}
        {suffix && value ? suffix : ""}
      </RenderValue>
    );
  };

  return (
    <>
      <JobInfoItemWrapper direction={direction} gap={1}>
        {label && (
          <Typography variant="cap12" color={black[70]}>
            {label}
            {required && (
              <Typography component="span" variant="cap12" sx={{ color: red[700], ml: 0.5 }}>
                *
              </Typography>
            )}
          </Typography>
        )}
        <Stack direction="row" justifyContent="space-between">
          <RenderComponent />
          <Stack>{endIcon}</Stack>
        </Stack>
        {isShowDivider && <DividerWrapper />}
      </JobInfoItemWrapper>
    </>
  );
};

const JobInfoItemWrapper = styled(Stack)<{ direction?: string }>`
  ${({ direction }) => (direction === "row" ? `align-items: center;` : ``)}
`;

const DividerWrapper = styled(Divider)`
  margin-top: 8px;
  width: 100%;
`;
